import { useState } from 'react';
import { motion } from 'framer-motion';

interface Service {
  id: number;
  title: string;
  description: string;
  image: string;
  features: string[];
}

const services: Service[] = [
  {
    id: 1,
    title: 'Fundamentals of AI Prompting',
    description: 'Perfect for teams new to AI. Learn the core principles of effective prompting, understand AI capabilities, and start seeing immediate productivity gains.',
    image: 'https://public.readdy.ai/ai/img_res/919659464c08077d5a4c970580aca4f7.jpg',
    features: ['Basic Prompt Structure', 'AI Tool Overview', 'Practical Exercises'],
  },
  {
    id: 2,
    title: 'Advanced Prompt Engineering',
    description: 'Take your AI skills to the next level. Master complex prompting techniques, chain-of-thought reasoning, and role-based prompting for sophisticated business applications.',
    image: 'https://readdy.ai/api/search-image?query=Advanced%20technology%20training%20session%20with%20professionals%20using%20AI%20tools%2C%20purple%20ambient%20lighting%2C%20sophisticated%20modern%20workspace%2C%20clean%20minimal%20background%2C%20high-tech%20atmosphere&width=500&height=625&seq=service-002&orientation=portrait',
    features: ['Advanced Techniques', 'Custom Workflows', 'Team Collaboration'],
  },
  {
    id: 3,
    title: 'Custom AI Integration Training',
    description: 'Tailored enterprise solutions. We analyze your business processes and create custom AI prompting frameworks that integrate seamlessly with your existing workflows.',
    image: 'https://readdy.ai/api/search-image?query=Enterprise%20business%20meeting%20with%20AI%20integration%20planning%2C%20deep%20blue%20professional%20environment%2C%20corporate%20setting%2C%20clean%20simple%20background%2C%20strategic%20planning%20atmosphere&width=500&height=625&seq=service-003&orientation=portrait',
    features: ['Custom Framework', 'Process Integration', 'Ongoing Support', 'Flexible Duration'],
  },
];

export default function ServicesSection() {
  const [activeCard, setActiveCard] = useState(2);

  return (
    <section id="services" className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <h2 className="text-5xl lg:text-6xl font-bold text-charcoal mb-6">
            Our Training Programs <span className="inline-block">🎯</span>
          </h2>
          <p className="text-xl text-gray-600">Choose the path that fits your team's needs</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-charcoal mb-6">Consulting Services</h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Comprehensive AI consulting solutions tailored to your business needs and growth objectives
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-xl mb-6">
              <i className="ri-lightbulb-line text-3xl text-primary"></i>
            </div>
            <h3 className="text-2xl font-bold text-charcoal mb-4">AI Strategy Development</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              We help you create clear AI roadmaps that match your business goals. We identify the best opportunities and prioritize projects that deliver real results.
            </p>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-primary mt-1"></i>
                <span>Strategic planning & roadmapping</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-primary mt-1"></i>
                <span>Technology assessment</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-primary mt-1"></i>
                <span>ROI analysis & forecasting</span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-secondary/10 rounded-xl mb-6">
              <i className="ri-team-line text-3xl text-secondary"></i>
            </div>
            <h3 className="text-2xl font-bold text-charcoal mb-4">Implementation Advisory</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              We guide your team through AI projects from start to finish. From choosing the right tools to launching successfully, we help you avoid common mistakes.
            </p>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-secondary mt-1"></i>
                <span>Vendor evaluation & selection</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-secondary mt-1"></i>
                <span>Project oversight & management</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-secondary mt-1"></i>
                <span>Risk mitigation strategies</span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-accent/10 rounded-xl mb-6">
              <i className="ri-line-chart-line text-3xl text-accent"></i>
            </div>
            <h3 className="text-2xl font-bold text-charcoal mb-4">Performance Optimization</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              We review your current AI systems and make them work better. Our goal is to improve accuracy, speed, and business results through smart improvements.
            </p>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-accent mt-1"></i>
                <span>System audits & diagnostics</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-accent mt-1"></i>
                <span>Performance tuning</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-accent mt-1"></i>
                <span>Continuous improvement plans</span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-xl mb-6">
              <i className="ri-shield-check-line text-3xl text-primary"></i>
            </div>
            <h3 className="text-2xl font-bold text-charcoal mb-4">Governance & Compliance</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              We establish AI governance frameworks, ethical guidelines, and compliance protocols to ensure responsible and sustainable AI adoption.
            </p>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-primary mt-1"></i>
                <span>Policy development</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-primary mt-1"></i>
                <span>Compliance frameworks</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-primary mt-1"></i>
                <span>Ethics & responsible AI</span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-secondary/10 rounded-xl mb-6">
              <i className="ri-graduation-cap-line text-3xl text-secondary"></i>
            </div>
            <h3 className="text-2xl font-bold text-charcoal mb-4">Executive Workshops</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              We deliver customized training programs for leadership teams, building AI literacy and strategic decision-making capabilities across your organization.
            </p>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-secondary mt-1"></i>
                <span>Leadership training programs</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-secondary mt-1"></i>
                <span>Strategic decision-making</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-secondary mt-1"></i>
                <span>Change management</span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-accent/10 rounded-xl mb-6">
              <i className="ri-customer-service-2-line text-3xl text-accent"></i>
            </div>
            <h3 className="text-2xl font-bold text-charcoal mb-4">Ongoing Advisory</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              We provide retained consulting services with regular strategic reviews, keeping you ahead of AI trends and continuously optimizing your initiatives.
            </p>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-accent mt-1"></i>
                <span>Monthly strategy sessions</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-accent mt-1"></i>
                <span>Priority email support</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-accent mt-1"></i>
                <span>Quarterly business reviews</span>
              </li>
            </ul>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onMouseEnter={() => setActiveCard(service.id)}
              className={`relative rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 ${
                activeCard === service.id ? 'md:col-span-1' : 'md:col-span-1'
              }`}
              style={{ aspectRatio: '4/5' }}
            >
              <div className="absolute inset-0">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-all duration-500 hover:brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{
                    opacity: activeCard === service.id ? 1 : 0,
                    height: activeCard === service.id ? 'auto' : 0,
                  }}
                  transition={{ duration: 0.4 }}
                  className="overflow-hidden"
                >
                  <p className="text-white/90 mb-4 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <i className="ri-check-line text-secondary"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}